import { useEffect } from "react";
import PhoneMockups from "../components/PhoneMockups";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";

const features = [
  {
    title: "Daily Manna",
    subhead: "Your Daily Spiritual Foundation",
    description:
      "Start every morning anchored in truth. Receive a fresh, daily Scripture touchpoint complete with plain-language context, key terms, and practical application questions.",
  },
  {
    title: "Daily Check-In",
    subhead: "60-Second Daily Alignment",
    description:
      "Rapidly assess your subjective state daily. Track your mood, physical energy, and core holistic pillars with zero friction.",
  },
  {
    title: "The Memory Vault",
    subhead: "Internalize the Word",
    description:
      "Never lose track of the Scriptures shaping your heart. Store, review, and lock foundational verses into long-term memory with structured recall tools.",
  },
  {
    title: "The Sanctuary Suite",
    subhead: "Distraction-Free Scripture Reader",
    description:
      "Immerse yourself in God’s Word without subscription ad bloat. Instantly toggle between 9 modern and traditional English translations—plus 3 original Hebrew and Koine Greek manuscript layers for deep study.",
  },
  {
    title: "Weekly Stewardship Mirror",
    subhead: "AI-Powered Cross-Correlation Audit",
    description:
      "Once a week, lock in your 30 fluid metric sliders across Spirit, Soul, and Body. Our intelligence engine cross-references your 7-day physical check-ins against your written reflection to reveal hidden behavioral patterns and deliver 3 targeted Micro-Disciplines.",
  },
  {
    title: "Barnabas Sync",
    subhead: "Scriptural Accountability",
    description:
      "Bring your inner circle into your stewardship journey. Share weekly climate scores and progress with trusted accountability partners to ensure you never walk alone.",
  },
];

const steps = [
  {
    number: "1",
    label: "Step 1",
    title: "Check In Daily (1 Min)",
    description:
      "Log your physical rest, mood, and core disciplines each morning.",
  },
  {
    number: "2",
    label: "Step 2",
    title: "Engage the Sanctuary & Vault",
    description:
      "Meditate on Daily Manna, memorize core passages in the Vault, and journal raw thoughts through scriptural logic.",
  },
  {
    number: "3",
    label: "Step 3",
    title: "Sabbath Calibration",
    description:
      "Review your Weekly Stewardship Mirror to receive personalized AI insights, identify lagging pillars, and set your 3 focal habits for the coming week.",
  },
];

function Home() {
  useEffect(() => {
    if (!window.location.hash) return;
    const id = window.location.hash.slice(1);
    const el = document.getElementById(id);
    if (el) {
      requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth" }));
    }
  }, []);

  return (
    <div className="page">
      <SiteHeader />

      <main id="top">
        <section className="hero">
          <div className="hero-atmosphere" />
          <div className="container hero-grid">
            <div className="hero-copy">
              <p className="overline reveal">Spirit · Soul · Body</p>
              <h1 className="hero-headline reveal reveal-delay-1">
                Steward Your Spirit, Soul, and Body.
              </h1>
              <p className="hero-sub reveal reveal-delay-2">
                A sacred digital ecosystem built to align your daily habits,
                deepen your walk in Scripture, and provide intelligent pastoral
                reflection—not just task lists.
              </p>
              <div className="hero-ctas reveal reveal-delay-3">
                <a href="#stewardship" className="btn btn-primary">
                  Begin Your Stewardship
                </a>
                <a href="#features" className="btn btn-ghost">
                  Explore Features
                </a>
              </div>
            </div>
            <div className="hero-visual reveal reveal-delay-2">
              <PhoneMockups />
            </div>
          </div>
        </section>

        <section className="ecosystem" id="features">
          <div className="container">
            <header className="section-head">
              <p className="overline">The Core Ecosystem</p>
              <h2 className="section-title">
                Seven disciplines, one unified temple.
              </h2>
            </header>

            <div className="feature-grid feature-grid--top">
              {features.slice(0, 3).map((feature) => (
                <article className="feature-card" key={feature.title}>
                  <span className="feature-accent" />
                  <h3 className="feature-title">{feature.title}</h3>
                  <p className="feature-subhead">{feature.subhead}</p>
                  <p className="feature-body">{feature.description}</p>
                </article>
              ))}
            </div>

            <article className="featured-card">
              <div className="featured-card__glow" />
              <div className="featured-card__copy">
                <p className="featured-label">Featured</p>
                <h3 className="feature-title">The Bridge Journal</h3>
                <p className="feature-subhead">
                  Scriptural Logic &amp; AI Processing
                </p>
              </div>
              <p className="feature-body featured-card__body">
                Move journaling beyond an echo chamber. Express raw thoughts,
                emotions, and daily pressures, then hit “Process” to bridge your
                real-time experiences to Biblical narratives, characters, and
                practical wisdom. Receive a ground-level “Third Voice”—anchored
                in the Holy Spirit through the Word—to break cycles of fear,
                stress, and anxiety.
              </p>
            </article>

            <div className="feature-grid feature-grid--bottom">
              {features.slice(3).map((feature) => (
                <article className="feature-card" key={feature.title}>
                  <span className="feature-accent" />
                  <h3 className="feature-title">{feature.title}</h3>
                  <p className="feature-subhead">{feature.subhead}</p>
                  <p className="feature-body">{feature.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="how" id="how-it-works">
          <div className="container">
            <header className="section-head section-head--center">
              <p className="overline">How It Works</p>
              <h2 className="section-title">
                Check In · Read &amp; Reflect · Calibrate
              </h2>
            </header>

            <div className="steps-grid">
              {steps.map((step) => (
                <article className="step-card" key={step.number}>
                  <span className="step-number">{step.number}</span>
                  <p className="step-label">{step.label}</p>
                  <h3 className="step-title">{step.title}</h3>
                  <p className="step-body">{step.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="promise" id="stewardship">
          <div className="container promise-inner">
            <h2 className="promise-title">A Note on Stewardship</h2>
            <blockquote className="promise-card">
              <p>
                “The Way 5:23 is built to treat your spirit, soul, and body as a
                unified temple. This platform serves strictly as a tool for
                alignment, reflection, and accountability—never a replacement
                for your personal, uninterrupted time with the Holy Spirit.”
              </p>
            </blockquote>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}

export default Home;
